import { alpha, extendTheme } from '@mui/material/styles';
import {
  grey,
  foamGreen,
  natomaGreen,
  natomaBlack,
  warning,
  error
} from './colors';
import { fontStyles, interFontFamily } from './fonts';
import { green, red, amber, neutral, blue } from './tokens/palette';
import { semanticLight, semanticDark } from './tokens/semantic';
import {
  CheckIcon,
  ChevronDownIcon,
  MinusIcon
} from '@natomalabs/natomui/src/icons';
import { shadows, shadowSm } from './shadows';
import React, { forwardRef } from 'react';
import type { LinkProps } from 'next/link';
import NextLink from 'next/link';
import { type LinkBaseProps } from '@mui/material';
import { greyDark, natomaGreenDark } from './darkMode/colors';
import type {
  ActionableTokens,
  InformationalTokens,
  SidenavTokens
} from './tokens/semantic';
import { SIDENAV_TOTAL_WIDTH } from './sidenav';

// See these MUI docs for customizing colors
// https://mui.com/material-ui/customization/palette/

/** Type to help integrate with MUI */
type LinkBehaviorProps = Omit<LinkProps, 'href'> &
  LinkBaseProps & { href: LinkProps['href'] };

/** Integrate to NextLink */
const LinkBehavior = forwardRef<HTMLAnchorElement, LinkBehaviorProps>(
  function LinkBehavior({ href, ...props }, ref) {
    // Check if the link is external
    const hrefString = href.toString();
    const isExternal = hrefString.startsWith('http');

    if (isExternal) {
      return <a ref={ref} href={hrefString} {...props} />;
    }

    return <NextLink ref={ref} href={href} {...props} />;
  }
);

const finalTheme = extendTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      '2xl': 1920,
      '3xl': 2560
    }
  },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.grey[100],
          borderRadius: `${theme.spacing(2)} !important`,
          boxShadow: 'none',
          width: '100%',
          '&:before': {
            display: 'none'
          },
          '&.Mui-expanded': {
            margin: 0
          }
        })
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          display: 'flex',
          gap: theme.spacing(1),
          padding: theme.spacing(1.5),
          minHeight: 'unset',
          '&.Mui-expanded': {
            minHeight: 'unset'
          }
        }),
        content: ({ theme }) => ({
          margin: 0,
          gap: theme.spacing(2),
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          order: 2,
          '&.Mui-expanded': {
            margin: 0
          }
        }),
        expandIconWrapper: {
          order: 1
        }
      }
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderTop: `1px solid ${grey[200]}`,
          padding: theme.spacing(1.5, 1.5, 1.5, 5.5),

          '& a': {
            color: blue[600],
            textDecoration: 'underline'
          },

          '& > ol': {
            padding: theme.spacing(0, 0, 0, 1.5),
            margin: 0
          },
          '& > ol > li': {
            fontWeight: fontStyles.textRegularMedium,
            fontSize: 12,
            margin: 0,
            padding: 0,

            '&:not(:last-child)': {
              marginBottom: theme.spacing(1)
            }
          }
        })
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: ({ theme }) => ({
          // Adjust padding when clear icon is present to accommodate both icons
          '&.MuiAutocomplete-hasClearIcon .MuiFormControl-root .MuiOutlinedInput-root':
            {
              paddingRight: theme.spacing(9.5) // 2 icons (4 spacing units each) + extra right padding (1.5 units)
            }
        }),
        popupIndicator: {
          border: 'none',
          '&:hover': {
            backgroundColor: 'transparent'
          },
          '&:disabled': {
            backgroundColor: 'transparent'
          }
        },
        clearIndicator: {
          border: 'none',
          '&:hover': {
            backgroundColor: 'transparent'
          }
        },
        tag: ({ theme }) => ({
          padding: theme.spacing(0.5, 1),
          borderRadius: theme.spacing(1),
          margin: 0
        }),
        input: ({ theme }) => ({
          height: theme.spacing(3.5),

          // Override the default padding of the input,
          // without resorting to !important
          '&.MuiOutlinedInput-input': {
            padding: theme.spacing(0, 1)
          }
        })
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          const secondary = theme.vars.palette.actionable.secondary;
          const danger = theme.vars.palette.actionable.danger;
          const borderColor = ownerState.error
            ? danger.default.border
            : secondary.default.border;
          const focusRing = ownerState.error
            ? danger.focusRing
            : secondary.focusRing;
          const heightStyles = ownerState.multiline
            ? { minHeight: '32px' }
            : {
                height: '32px',
                minHeight: '32px',
                boxSizing: 'border-box' as const
              };

          return {
            ...heightStyles,
            borderRadius: '8px',
            backgroundColor: secondary.default.background,
            alignItems: 'center',
            paddingLeft: theme.spacing(1.5),
            paddingRight: theme.spacing(1.5),
            gap: theme.spacing(1),
            border: `1px solid ${borderColor}`,
            outlineWidth: 0,
            outlineStyle: 'solid',
            outlineColor: focusRing,
            transition: theme.transitions.create(
              ['border-color', 'outline-width'],
              {
                duration: theme.transitions.duration.short
              }
            ),
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none'
            },
            '&:hover': {
              borderColor: ownerState.error
                ? danger.default.border
                : secondary.active.border,
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            },
            '&.Mui-focused': {
              borderColor: ownerState.error
                ? danger.active.border
                : secondary.active.border,
              outlineWidth: 4,
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            },
            '&.Mui-disabled': {
              cursor: 'not-allowed',
              borderColor: secondary.default.border,
              backgroundColor: secondary.default.background,
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            },
            '&.Mui-error': {
              borderColor: danger.default.border,
              backgroundColor: secondary.default.background
            },
            '&.Mui-error.Mui-focused': {
              outline: `4px solid ${danger.focusRing}`
            }
          };
        },
        input: ({ theme }) => ({
          ...fontStyles.textRegularNormal,
          padding: 0,
          height: 'auto',
          color: theme.vars.palette.text.primary,
          '&::placeholder': {
            color: theme.vars.palette.text.disabled,
            opacity: 1
          },
          '&.Mui-disabled': {
            color: theme.vars.palette.text.disabled,
            WebkitTextFillColor: theme.vars.palette.text.disabled
          }
        })
      }
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiButton: {
      defaultProps: {
        variant: 'secondary',
        size: 'default'
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          let props = {};

          // Stellar sizes: small = 24px, default/regular = 32px
          if (ownerState.size === 'small') {
            props = {
              minHeight: '24px',
              padding: '4px 8px',
              gap: '6px',
              borderRadius: '8px',
              boxSizing: 'border-box' as const,
              ...fontStyles.base,
              '& .MuiSvgIcon-root': {
                fontSize: '16px'
              }
            };
          } else {
            props = {
              minHeight: '32px',
              padding: '6px 12px',
              gap: '8px',
              borderRadius: '8px',
              boxSizing: 'border-box' as const,
              ...fontStyles.textRegularMedium,
              '& .MuiSvgIcon-root': {
                fontSize: '16px'
              }
            };
          }

          // Handle Variants
          if (ownerState.variant === 'primary') {
            props = {
              ...props,
              border: 0,
              backgroundColor:
                theme.vars.palette.actionable.primary.default.background,
              color: theme.vars.palette.actionable.primary.default.foreground,
              outlineColor: theme.vars.palette.actionable.primary.focusRing,
              '&:hover': {
                backgroundColor:
                  theme.vars.palette.actionable.primary.active.background,
                color: theme.vars.palette.actionable.primary.active.foreground
              },
              '&:focus-visible': {
                outlineWidth: 4
              },
              '&:disabled': {
                backgroundColor:
                  theme.vars.palette.actionable.primary.disabled.background,
                color: theme.vars.palette.actionable.primary.disabled.foreground
              }
            };
          } else if (ownerState.variant === 'secondary') {
            props = {
              ...props,
              backgroundColor:
                theme.vars.palette.actionable.secondary.default.background,
              color: theme.vars.palette.actionable.secondary.default.foreground,
              outlineColor: theme.vars.palette.actionable.secondary.focusRing,
              // Ensure border exists if logic relies on it, or handle transparent
              border:
                theme.vars.palette.actionable.secondary.default.border !==
                'transparent'
                  ? `1px solid ${theme.vars.palette.actionable.secondary.default.border}`
                  : 'none',
              '&:hover': {
                backgroundColor:
                  theme.vars.palette.actionable.secondary.active.background,
                color:
                  theme.vars.palette.actionable.secondary.active.foreground,
                borderColor:
                  theme.vars.palette.actionable.secondary.active.border !==
                  'transparent'
                    ? theme.vars.palette.actionable.secondary.active.border
                    : 'none'
              },
              '&:focus-visible': {
                outlineWidth: 4,
                borderColor:
                  theme.vars.palette.actionable.secondary.active.border !==
                  'transparent'
                    ? theme.vars.palette.actionable.secondary.active.border
                    : undefined
              },
              '&:disabled': {
                backgroundColor:
                  theme.vars.palette.actionable.secondary.disabled.background,
                color:
                  theme.vars.palette.actionable.secondary.disabled.foreground,
                borderColor:
                  theme.vars.palette.actionable.secondary.disabled.border !==
                  'transparent'
                    ? theme.vars.palette.actionable.secondary.disabled.border
                    : 'none'
              }
            };
          } else if (ownerState.variant === 'tertiary') {
            props = {
              ...props,
              backgroundColor:
                theme.vars.palette.actionable.tertiary.default.background,
              color: theme.vars.palette.actionable.tertiary.default.foreground,
              outlineColor: theme.vars.palette.actionable.tertiary.focusRing,
              border:
                theme.vars.palette.actionable.tertiary.default.border !==
                'transparent'
                  ? `1px solid ${theme.vars.palette.actionable.tertiary.default.border}`
                  : 'none',
              '&:hover': {
                backgroundColor:
                  theme.vars.palette.actionable.tertiary.active.background,
                color: theme.vars.palette.actionable.tertiary.active.foreground
              },
              '&:focus-visible': {
                outlineWidth: 4
              },
              '&:disabled': {
                color:
                  theme.vars.palette.actionable.tertiary.disabled.foreground,
                backgroundColor:
                  theme.vars.palette.actionable.tertiary.disabled.background
              }
            };
          } else if (ownerState.variant === 'danger') {
            props = {
              ...props,
              backgroundColor:
                theme.vars.palette.actionable.danger.default.background,
              color: theme.vars.palette.actionable.danger.default.foreground,
              outlineColor: theme.vars.palette.actionable.danger.focusRing,
              border:
                theme.vars.palette.actionable.danger.default.border !==
                'transparent'
                  ? `1px solid ${theme.vars.palette.actionable.danger.default.border}`
                  : 'none',
              '&:hover': {
                backgroundColor:
                  theme.vars.palette.actionable.danger.active.background,
                color: theme.vars.palette.actionable.danger.active.foreground,
                borderColor:
                  theme.vars.palette.actionable.danger.active.border !==
                  'transparent'
                    ? theme.vars.palette.actionable.danger.active.border
                    : 'none'
              },
              '&:focus-visible': {
                outlineWidth: 4,
                borderColor:
                  theme.vars.palette.actionable.danger.active.border !==
                  'transparent'
                    ? theme.vars.palette.actionable.danger.active.border
                    : undefined
              },
              '&:disabled': {
                color: theme.vars.palette.actionable.danger.disabled.foreground,
                backgroundColor:
                  theme.vars.palette.actionable.danger.disabled.background,
                borderColor:
                  theme.vars.palette.actionable.danger.disabled.border !==
                  'transparent'
                    ? theme.vars.palette.actionable.danger.disabled.border
                    : 'none'
              }
            };
          }

          return {
            flexShrink: 0,
            boxShadow: 'none',
            textTransform: 'none',
            outlineWidth: 0,
            outlineStyle: 'solid',
            transition: theme.transitions.create(
              ['background-color', 'color', 'border-color', 'outline-width'],
              {
                duration: theme.transitions.duration.short
              }
            ),
            '&.Mui-disabled': {
              pointerEvents: 'unset',
              cursor: 'not-allowed'
            },
            ...props
          };
        },
        startIcon: {
          margin: 0
        },
        endIcon: {
          margin: 0
        }
      }
    },
    MuiIconButton: {
      defaultProps: {
        size: 'medium'
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          let props = {};
          if (ownerState.size === 'small') {
            props = {
              width: '32px',
              height: '32px',
              padding: theme.spacing(0.875)
            };
          } else if (ownerState.size === 'medium') {
            props = {
              width: '40px',
              height: '40px',
              padding: theme.spacing(1.5)
            };
          } else if (ownerState.size === 'large') {
            props = {
              width: '44px',
              height: '44px',
              padding: theme.spacing(1.5)
            };
          }

          if (ownerState.color === 'error') {
            props = {
              color: error[600],
              border: `1px solid ${error[600]}`,
              backgroundColor: alpha(error[600], 0.1),
              '&:hover': {
                color: theme.palette.common.white,
                backgroundColor: error[600]
              },
              '&:disabled': {
                color: error[400],
                backgroundColor: error[25],
                borderColor: error[400]
              }
            };
          }

          return {
            borderRadius: theme.spacing(1.5),
            boxSizing: 'border-box',
            border: `1px solid ${theme.vars.palette.divider}`,
            color: theme.vars.palette.text.secondary,
            backgroundColor: 'inherit',
            transition: theme.transitions.create(
              ['background-color', 'color'],
              {
                duration: theme.transitions.duration.short
              }
            ),
            '&:hover': {
              backgroundColor: theme.vars.palette.background.hover,
              color: theme.vars.palette.text.primary
            },
            '&.Mui-focused': {
              color: grey[600],
              backgroundColor: grey[100]
            },
            '&.Mui-disabled': {
              color: theme.vars.palette.text.disabled,
              backgroundColor: theme.vars.palette.background.disabled,
              pointerEvents: 'unset',
              cursor: 'not-allowed'
            },
            '& .MuiTouchRipple-root .MuiTouchRipple-child': {
              borderRadius: theme.spacing(1.5)
            },
            ...props
          };
        }
      }
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          display: 'flex',
          alignItems: 'center',
          flex: 'unset',
          width: 'auto',
          backgroundColor: theme.vars.palette.background.surface,
          border: `1px solid ${theme.vars.palette.actionable.secondary.default.border}`,
          borderRadius: theme.spacing(1.5),
          padding: theme.spacing(0.5)
        })
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          display: 'flex',
          alignItems: 'center',
          textTransform: 'none',
          backgroundColor: 'transparent',
          color: theme.vars.palette.actionable.secondary.default.foreground,
          // !important is the most efficient way to override MUI's default border radius handling
          // for toggle buttons. It's acceptable here because our design system prohibits
          // downstream changes to toggle button styles, so we should never need to override this.
          borderRadius: `${theme.spacing(1)} !important`,
          minWidth: 0,
          minHeight: 0,
          padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
          border: '1px solid transparent',
          marginRight: theme.spacing(0.5),
          '&:last-child': {
            marginRight: 0
          },
          ...fontStyles.textRegularMedium,
          '&:disabled': {
            pointerEvents: 'auto',
            color: theme.vars.palette.actionable.secondary.disabled.foreground,
            backgroundColor:
              theme.vars.palette.actionable.secondary.disabled.background,
            cursor: 'not-allowed',
            '&:hover': {
              color:
                theme.vars.palette.actionable.secondary.disabled.foreground,
              backgroundColor:
                theme.vars.palette.actionable.secondary.disabled.background
            }
          },
          '&.Mui-selected': {
            color: theme.vars.palette.actionable.secondary.active.foreground,
            backgroundColor:
              theme.vars.palette.actionable.secondary.active.background,
            borderColor: theme.vars.palette.actionable.secondary.active.border,
            '&:hover': {
              backgroundColor:
                theme.vars.palette.actionable.secondary.active.background,
              borderColor:
                theme.vars.palette.actionable.secondary.active.border,
              color: theme.vars.palette.actionable.secondary.active.foreground
            }
          },
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.vars.palette.actionable.secondary.active.foreground,
            borderColor: theme.vars.palette.actionable.secondary.default.border
          }
        })
      }
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2, 3),
          border: `1px solid ${theme.vars.palette.divider}`,
          background: theme.vars.palette.background.paper
        })
      }
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:disabled': {
            backgroundColor: theme.vars.palette.background.disabled,
            borderColor: grey[100]
          }
        })
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          maxWidth: theme.spacing(60),
          borderRadius: theme.spacing(3),
          borderColor: theme.vars.palette.divider,
          [theme.getColorSchemeSelector('dark')]: {
            background: theme.vars.palette.grey[100]
          }
        }),
        root: {
          'aria-modal': true
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingBottom: theme.spacing(0.5) // 4px (pb: 1 in 4px scale)
        })
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(3, 0, 0, 0),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        })
      }
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior
      },
      styleOverrides: {
        root: ({ theme }) => ({
          textDecoration: 'none',
          color: theme.vars.palette.text.primary,
          '&:hover': {
            color: theme.vars.palette.text.secondary
          }
        })
      }
    },
    MuiPaper: {
      styleOverrides: {
        outlined: ({ theme }) => ({
          '&.MuiCard-root': {
            borderRadius: theme.spacing(3),
            [theme.getColorSchemeSelector('light')]: {
              borderColor: theme.vars.palette.grey[300]
            },
            [theme.getColorSchemeSelector('dark')]: {
              borderColor: theme.vars.palette.grey[700]
            }
          }
        })
      }
    },
    MuiSkeleton: {
      defaultProps: {
        variant: 'rounded',
        animation: 'wave'
      },
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.getColorSchemeSelector('light')]: {
            backgroundColor: theme.vars.palette.grey[100]
          },
          [theme.getColorSchemeSelector('dark')]: {
            backgroundColor: theme.vars.palette.grey[300]
          }
        })
      }
    },
    MuiSnackbar: {
      defaultProps: {
        autoHideDuration: 4000
      }
    },
    MuiSnackbarContent: {
      defaultProps: {
        variant: 'info'
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.spacing(3),
          borderWidth: 1,
          borderStyle: 'solid',
          boxShadow: theme.shadows[1],
          maxWidth: theme.spacing(80),
          gap: theme.spacing(1),
          alignItems: 'flex-start',
          flexWrap: 'nowrap',
          ['&:last-child']: {
            marginBottom: 0
          },
          variants: [
            {
              props: { variant: 'success' },
              style: {
                borderColor: theme.palette.green[400],
                background: theme.palette.green[50]
              }
            },
            {
              props: { variant: 'warning' },
              style: {
                borderColor: theme.palette.amber[400],
                background: theme.palette.amber[50]
              }
            },
            {
              props: { variant: 'error' },
              style: {
                borderColor: theme.palette.red[400],
                background: theme.palette.red[50]
              }
            },
            {
              props: { variant: 'info' },
              style: {
                borderColor: theme.palette.blue[400],
                background: theme.palette.blue[50]
              }
            }
          ]
        }),
        action: ({ theme }) => ({
          marginTop: theme.spacing(1)
        }),
        message: {}
      }
    },
    MuiTableContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          border: `1px solid ${theme.vars.palette.divider}`
        })
      }
    },
    MuiTable: {
      variants: [
        {
          props: { className: 'clickable' },
          style: ({ theme }) => ({
            '& .MuiTableBody-root': {
              cursor: 'pointer',
              '& .MuiTableRow-root': {
                '&:hover': {
                  backgroundColor: theme.vars.palette.background.hover
                }
              }
            }
          })
        }
      ],
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'column'
        }
      }
    },
    MuiTableRow: {
      variants: [
        {
          props: { className: 'clickable' },
          style: ({ theme }) => ({
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.vars.palette.background.hover
            }
          })
        },
        {
          props: { className: 'nonclickable' },
          style: ({ theme }) => ({
            cursor: 'not-allowed',
            '&:hover': {
              backgroundColor: 'transparent'
            },
            [theme.getColorSchemeSelector('light')]: {
              color: theme.vars.palette.grey[400],
              '& .MuiTableCell-root': {
                color: theme.vars.palette.grey[400]
              }
            },
            [theme.getColorSchemeSelector('dark')]: {
              color: theme.vars.palette.grey[900],
              '& .MuiTableCell-root': {
                color: theme.vars.palette.grey[900]
              }
            }
          })
        },
        {
          props: { className: 'selected' },
          style: ({ theme }) => ({
            cursor: 'pointer',
            backgroundColor: theme.vars.palette.background.selected
          })
        }
      ],
      styleOverrides: {
        root: {
          display: 'flex',
          alignItems: 'center'
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: theme.vars.palette.background.paper,
          borderBottom: `1px solid ${theme.vars.palette.divider}`,
          [theme.getColorSchemeSelector('light')]: {
            color: theme.vars.palette.grey[500]
          },
          [theme.getColorSchemeSelector('dark')]: {
            color: theme.vars.palette.grey[600]
          }
        })
      }
    },
    MuiTableBody: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.vars.palette.text.primary
        })
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...fontStyles.textMiniMedium,
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          /**
           * Moving forward, we are going to have to wrap all raw text in <span> or  equivalent
           * This is what applies the ellipsis - it needs a wrapper to determine its width overflow
           */
          '& *': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis'
          },
          flex: 1,
          fontWeight: 500,
          borderBottom: 0,
          minHeight: 60,
          padding: theme.spacing(2),
          '& .MuiChip-root': {
            '&:hover': {
              backgroundColor: theme.vars.palette.background.darkHover
            }
          }
        }),
        head: ({ theme }) => ({
          gap: theme.spacing(0.5),
          [theme.getColorSchemeSelector('light')]: {
            color: theme.vars.palette.grey[500]
          },
          [theme.getColorSchemeSelector('dark')]: {
            color: theme.vars.palette.grey[600]
          }
        }),
        body: ({ theme }) => ({
          color: theme.vars.palette.text.primary
        })
      }
    },
    MuiTabs: {
      defaultProps: {
        variant: 'scrollable',
        scrollButtons: false,
        TabIndicatorProps: {
          style: { display: 'none' }
        }
      },
      styleOverrides: {
        root: ({ theme }) => ({
          display: 'flex',
          alignItems: 'center',
          flex: 'unset',
          width: 'auto',

          '& > div': {
            backgroundColor: theme.vars.palette.background.surface,
            border: `1px solid ${theme.vars.palette.actionable.secondary.default.border}`,
            borderRadius: theme.spacing(1.5),
            padding: theme.spacing(0.5)
          }
        }),
        scrollableX: {
          marginBottom: '0 !important'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          display: 'flex',
          alignItems: 'center',
          textTransform: 'none',
          backgroundColor: 'transparent',
          color: theme.vars.palette.actionable.secondary.default.foreground,
          borderRadius: theme.spacing(1),
          minWidth: 0,
          minHeight: 0,
          padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
          border: '1px solid transparent',
          marginRight: theme.spacing(0.5),
          '&:last-child': {
            marginRight: 0
          },
          ...fontStyles.textRegularMedium,
          '&:disabled': {
            pointerEvents: 'auto',
            color: theme.vars.palette.actionable.secondary.disabled.foreground,
            backgroundColor:
              theme.vars.palette.actionable.secondary.disabled.background,
            cursor: 'not-allowed',
            '&:hover': {
              color:
                theme.vars.palette.actionable.secondary.disabled.foreground,
              backgroundColor:
                theme.vars.palette.actionable.secondary.disabled.background
            }
          },
          '&.Mui-selected': {
            color: theme.vars.palette.actionable.secondary.active.foreground,
            backgroundColor:
              theme.vars.palette.actionable.secondary.active.background,
            borderColor: theme.vars.palette.actionable.secondary.active.border,
            '&:hover': {
              backgroundColor:
                theme.vars.palette.actionable.secondary.active.background,
              borderColor:
                theme.vars.palette.actionable.secondary.active.border,
              color: theme.vars.palette.actionable.secondary.active.foreground
            }
          },
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.vars.palette.actionable.secondary.active.foreground,
            borderColor: theme.vars.palette.actionable.secondary.default.border
          }
        })
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          width: '100%',
          height: `calc(100vh - ${theme.spacing(2)})`,
          maxWidth: `calc(100vw - ${SIDENAV_TOTAL_WIDTH}px)`,
          margin: theme.spacing(1),
          borderRadius: theme.spacing(2),
          [theme.breakpoints.down('lg')]: {
            maxWidth: `calc(100vw - ${theme.spacing(2)})`
          },
          [theme.breakpoints.up('xl')]: {
            maxWidth: `calc(50vw - ${SIDENAV_TOTAL_WIDTH}px)`
          },
          background: theme.vars.palette.background.paper
        })
      }
    },
    MuiChip: {
      defaultProps: {
        variant: 'outlined'
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.spacing(0.5),
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: theme.vars.palette.divider,
          display: 'flex',
          alignItems: 'center',
          height: 'auto',
          padding: `${theme.spacing(0.25)} ${theme.spacing(0.75)}`,
          gap: theme.spacing(0.5)
        }),
        label: {
          ...fontStyles.textMicroNormal,
          padding: 0
        },
        // Informational chip variants (default | info | success | warning | danger)
        colorDefault: ({ theme }) => ({
          backgroundColor: theme.vars.palette.informational.default.background,
          color: theme.vars.palette.informational.default.foreground,
          borderColor: theme.vars.palette.informational.default.border,
          '&:hover': {
            backgroundColor: theme.vars.palette.background.hover
          }
        }),
        colorInfo: ({ theme }) => ({
          backgroundColor: theme.vars.palette.informational.info.background,
          color: theme.vars.palette.informational.info.foreground,
          borderColor: theme.vars.palette.informational.info.border,
          '&:hover': {
            backgroundColor: theme.vars.palette.background.hover
          }
        }),
        colorSuccess: ({ theme }) => ({
          backgroundColor: theme.vars.palette.informational.success.background,
          color: theme.vars.palette.informational.success.foreground,
          borderColor: theme.vars.palette.informational.success.border,
          '&:hover': {
            backgroundColor: theme.vars.palette.background.hover
          }
        }),
        colorWarning: ({ theme }) => ({
          backgroundColor: theme.vars.palette.informational.warning.background,
          color: theme.vars.palette.informational.warning.foreground,
          borderColor: theme.vars.palette.informational.warning.border,
          '&:hover': {
            backgroundColor: theme.vars.palette.background.hover
          }
        }),
        colorError: ({ theme }) => ({
          backgroundColor: theme.vars.palette.informational.danger.background,
          color: theme.vars.palette.informational.danger.foreground,
          borderColor: theme.vars.palette.informational.danger.border,
          '&:hover': {
            backgroundColor: theme.vars.palette.background.hover
          }
        })
      }
    },
    MuiMenu: {
      defaultProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right'
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'right'
        }
      },
      styleOverrides: {
        root: ({ theme }) => ({
          marginTop: theme.spacing(0.5)
        }),
        list: {
          padding: 0
        },
        paper: ({ theme }) => ({
          minWidth: theme.spacing(10.5),
          padding: theme.spacing(0.5)
        })
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: theme.spacing(4.5),
          padding: theme.spacing(1, 1.5),
          borderRadius: theme.spacing(1.5),
          gap: theme.spacing(1),
          color: theme.vars.palette.text.secondary
        })
      }
    },
    MuiPopover: {
      defaultProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left'
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left'
        }
      },
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(1, 1.5),
          borderRadius: theme.spacing(1.5),
          minWidth: 84,
          marginTop: theme.spacing(0.5)
        }),
        paper: ({ theme }) => ({
          border: `1px solid ${theme.vars.palette.divider}`,
          background: theme.vars.palette.background.paper
        })
      }
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: ChevronDownIcon
      },
      styleOverrides: {
        select: ({ theme }) => ({
          ...fontStyles.textRegularNormal,
          padding: 0,
          paddingRight: theme.spacing(5),
          color: theme.vars.palette.text.secondary,
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          '&:focus': {
            outline: 'none',
            backgroundColor: 'transparent'
          },
          '&.Mui-disabled': {
            cursor: 'not-allowed',
            color: theme.vars.palette.text.disabled,
            backgroundColor: 'transparent'
          }
        }),
        root: ({ theme }) => ({
          borderRadius: '8px',
          border: `1px solid ${theme.vars.palette.actionable.secondary.default.border}`,
          backgroundColor:
            theme.vars.palette.actionable.secondary.default.background,
          outlineWidth: 0,
          outlineStyle: 'solid',
          outlineColor: theme.vars.palette.actionable.secondary.focusRing,
          transition: 'outline-width 0.1s ease-out',
          height: '32px',
          minHeight: '32px',
          '& svg': {
            position: 'absolute',
            right: theme.spacing(2),
            pointerEvents: 'none',
            color: theme.vars.palette.text.secondary
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none'
          },
          '&:hover': {
            backgroundColor:
              theme.vars.palette.actionable.secondary.active.background,
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none'
            }
          },
          '&.Mui-focused': {
            borderColor: theme.vars.palette.actionable.secondary.active.border,
            outlineWidth: 4,
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none'
            }
          },
          '&.Mui-disabled': {
            cursor: 'not-allowed',
            backgroundColor:
              theme.vars.palette.actionable.secondary.disabled.background,
            borderColor:
              theme.vars.palette.actionable.secondary.disabled.border,
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none'
            }
          },
          '&.Mui-error': {
            borderColor: theme.vars.palette.actionable.danger.default.border,
            backgroundColor:
              theme.vars.palette.actionable.danger.default.background
          },
          '&.Mui-error.Mui-focused': {
            outline: `4px solid ${theme.vars.palette.actionable.danger.focusRing}`
          },
          '&.Mui-error.Mui-disabled': {
            borderColor: theme.vars.palette.actionable.danger.disabled.border,
            backgroundColor:
              theme.vars.palette.actionable.danger.disabled.background
          }
        })
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          ...fontStyles.textMicroNormal,
          maxWidth: '350px',
          padding: theme.spacing(1),
          borderRadius: theme.spacing(1.5),
          backgroundColor: theme.vars.palette.background.paper,
          border: `1px solid ${theme.vars.palette.divider}`,
          color: theme.vars.palette.text.secondary,
          boxShadow: shadowSm,
          margin: 0
        }),
        popper: {
          '&&[data-popper-placement*="left"] .MuiTooltip-tooltip': {
            marginRight: '4px'
          },
          '&&[data-popper-placement*="right"] .MuiTooltip-tooltip': {
            marginLeft: '4px'
          },
          '&&[data-popper-placement*="top"] .MuiTooltip-tooltip': {
            marginBottom: '4px'
          },
          '&&[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
            marginTop: '4px'
          }
        }
      }
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.vars.palette.text.primary,
          '& .MuiBreadcrumbs-separator': {
            color: theme.vars.palette.grey[400],
            ...fontStyles.displayExtraSmallMedium
          },
          '& .MuiBreadcrumbs-li, a, div': {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            maxWidth: '240px',
            '& h3': {
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis'
            }
          }
        })
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.vars.palette.divider
        })
      }
    },
    MuiCheckbox: {
      defaultProps: {
        icon: <></>,
        checkedIcon: <CheckIcon />,
        indeterminateIcon: <MinusIcon />,
        disableRipple: true
      },
      styleOverrides: {
        root: ({ theme }) => ({
          height: theme.spacing(2.5),
          width: theme.spacing(2.5),
          padding: 0,

          svg: {
            position: 'relative',
            pointerEvents: 'none',
            zIndex: 1,
            width: theme.spacing(1.25),
            height: theme.spacing(1.25)
          },

          '& svg path': {
            strokeWidth: 3
          },

          // Unchecked, enabled (default)
          '& input': {
            appearance: 'none',
            opacity: 1,
            width: '100%',
            height: '100%',
            borderRadius: theme.spacing(0.75),
            borderWidth: theme.spacing(0.25),
            borderStyle: 'solid',
            borderColor: theme.vars.palette.actionable.secondary.default.border,
            backgroundColor:
              theme.vars.palette.actionable.secondary.default.background,
            outlineWidth: 0,
            outlineStyle: 'solid',
            outlineColor: theme.vars.palette.actionable.secondary.focusRing,
            outlineOffset: 0,
            transition: 'outline-width 0.1s ease-out',
            // Unchecked, hover
            '&:hover': {
              backgroundColor:
                theme.vars.palette.actionable.secondary.active.background,
              borderColor: theme.vars.palette.actionable.secondary.active.border
            },
            // Unchecked, focus
            '&:focus-visible': {
              outline: `4px solid ${theme.vars.palette.actionable.secondary.focusRing}`
            },
            // Unchecked, disabled
            '&:disabled': {
              backgroundColor:
                theme.vars.palette.actionable.secondary.disabled.background,
              borderColor:
                theme.vars.palette.actionable.secondary.disabled.border
            }
          },

          // Unchecked, error
          '&.Mui-error input': {
            borderColor: theme.vars.palette.actionable.danger.default.border,
            backgroundColor:
              theme.vars.palette.actionable.danger.default.background
          },
          // Unchecked, error focus
          '&.Mui-error input:focus-visible': {
            outline: `4px solid ${theme.vars.palette.actionable.danger.focusRing}`
          },

          // Unchecked, disabled + error
          '&.Mui-error.Mui-disabled input': {
            borderColor: theme.vars.palette.actionable.danger.disabled.border,
            backgroundColor:
              theme.vars.palette.actionable.danger.disabled.background
          },

          // Checked, enabled (default)
          '&.Mui-checked:not(.Mui-disabled):not(.Mui-error) input': {
            borderColor: theme.vars.palette.actionable.primary.default.border
          },
          // Checked, hover
          '&.Mui-checked:not(.Mui-disabled):not(.Mui-error) input:hover': {
            borderColor: theme.vars.palette.actionable.primary.active.border
          },
          // Checked, focus
          '&.Mui-checked:not(.Mui-disabled):not(.Mui-error) input:focus-visible':
            {
              outline: `4px solid ${theme.vars.palette.actionable.primary.focusRing}`
            },
          // Checked, disabled
          '&.Mui-checked.Mui-disabled:not(.Mui-error) input': {
            borderColor: theme.vars.palette.actionable.primary.disabled.border
          },
          // Checked, error
          '&.Mui-checked.Mui-error:not(.Mui-disabled) input': {
            borderColor: theme.vars.palette.actionable.danger.default.border
          },
          // Checked, error focus
          '&.Mui-checked.Mui-error:not(.Mui-disabled) input:focus-visible': {
            outline: `4px solid ${theme.vars.palette.actionable.danger.focusRing}`
          },
          // Checked, disabled + error
          '&.Mui-checked.Mui-error.Mui-disabled input': {
            borderColor: theme.vars.palette.actionable.danger.disabled.border
          },

          // Indeterminate, enabled (default)
          '&.MuiCheckbox-indeterminate:not(.Mui-disabled):not(.Mui-error) input':
            {
              borderColor: theme.vars.palette.actionable.primary.default.border
            },
          // Indeterminate, hover
          '&.MuiCheckbox-indeterminate:not(.Mui-disabled):not(.Mui-error) input:hover':
            {
              borderColor: theme.vars.palette.actionable.primary.active.border
            },
          // Indeterminate, focus
          '&.MuiCheckbox-indeterminate:not(.Mui-disabled):not(.Mui-error) input:focus-visible':
            {
              outline: `4px solid ${theme.vars.palette.actionable.primary.focusRing}`
            },
          // Indeterminate, disabled
          '&.MuiCheckbox-indeterminate.Mui-disabled:not(.Mui-error) input': {
            borderColor: theme.vars.palette.actionable.primary.disabled.border
          },
          // Indeterminate, error
          '&.MuiCheckbox-indeterminate.Mui-error:not(.Mui-disabled) input': {
            borderColor: theme.vars.palette.actionable.danger.default.border
          },
          // Indeterminate, error focus
          '&.MuiCheckbox-indeterminate.Mui-error:not(.Mui-disabled) input:focus-visible':
            {
              outline: `4px solid ${theme.vars.palette.actionable.danger.focusRing}`
            },
          // Indeterminate, disabled + error
          '&.MuiCheckbox-indeterminate.Mui-error.Mui-disabled input': {
            borderColor: theme.vars.palette.actionable.danger.disabled.border
          }
        })
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          '& .MuiAlert-action': {
            paddingTop: '2px'
          }
        }
      }
    },
    MuiSwitch: {
      variants: [
        {
          props: { className: 'ios-switch' },
          style: ({ theme }) => ({
            width: theme.spacing(4.5),
            height: theme.spacing(2.5),
            padding: 0,
            '& .MuiSwitch-switchBase': {
              padding: 2,
              margin: 0,
              width: theme.spacing(4.5),
              height: theme.spacing(2.5),
              borderRadius: theme.spacing(1.25),
              justifyContent: 'flex-start',
              transitionDuration: '300ms',
              '&:hover': {
                backgroundColor: 'transparent'
              },
              '&.Mui-checked': {
                transform: 'none',
                color: '#fff',
                '& + .MuiSwitch-track': {
                  backgroundColor: theme.vars.palette.primary,
                  opacity: 1,
                  border: 0
                },
                '& .MuiSwitch-thumb': {
                  boxSizing: 'border-box',
                  color: theme.vars.palette.text.primary,
                  width: theme.spacing(2),
                  height: theme.spacing(2),
                  transform: 'translateX(16px)'
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                  opacity: 0.5
                }
              },
              '&.Mui-disabled .MuiSwitch-thumb': {
                color: theme.vars.palette.grey[100]
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.7
              },
              '&.Mui-disabled': {
                pointerEvents: 'unset',
                cursor: 'not-allowed'
              }
            },
            '& .MuiSwitch-thumb': {
              boxSizing: 'border-box',
              color: theme.vars.palette.grey[300],
              width: theme.spacing(2),
              height: theme.spacing(2),
              transition: theme.transitions.create(['transform'], {
                duration: 300
              })
            },
            '& .MuiSwitch-track': {
              borderRadius: 26 / 2,
              backgroundColor: theme.vars.palette.background.default,
              opacity: 1,
              transition: theme.transitions.create(['background-color'], {
                duration: 500
              })
            }
          })
        }
      ]
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          heading: 'h6',
          subheading: 'h6',
          body: 'p',
          base: 'span'
        }
      }
    }
  },
  shape: { borderRadius: 12 },
  colorSchemes: {
    light: {
      palette: {
        common: {
          natomaBlack
        },
        primary: {
          main: natomaGreen
        },
        secondary: {
          main: grey[500]
        },
        error: {
          main: error['500']
        },
        warning: {
          main: warning['500'],
          light: warning['200'],
          ...warning
        },
        info: {
          main: grey[400]
        },
        text: {
          primary: semanticLight.text.regular,
          secondary: semanticLight.text.recessive,
          ternary: grey[500],
          disabled: grey[400]
        },
        // Legacy color palettes (kept for backward compatibility)
        blue,
        grey,
        foamGreen,
        // New palette tokens from Figma
        green,
        red,
        amber,
        neutral,
        brand: semanticLight.brand,
        divider: semanticLight.border.subtle,
        background: {
          default: semanticLight.background.page,
          paper: semanticLight.background.paper,
          surface: semanticLight.background.surface,
          hover: grey[50],
          darkHover: grey[200],
          disabled: grey[100],
          selected: grey[200]
        },
        // Actionable state tokens
        actionable: semanticLight.actionable,
        // Informational state tokens
        informational: semanticLight.informational,
        // Sidenav chrome tokens
        sidenav: semanticLight.sidenav,
        // For Chip color="danger" (uses informational.danger styling)
        danger: {
          main: semanticLight.informational.danger.foreground
        }
      }
    },
    dark: {
      palette: {
        common: {
          natomaBlack
        },
        primary: {
          main: natomaGreenDark
        },
        secondary: {
          main: grey[500]
        },
        error: {
          main: error['500']
        },
        warning: {
          main: warning['500'],
          light: warning['200'],
          ...warning
        },
        info: {
          main: grey[400]
        },
        text: {
          primary: semanticDark.text.regular,
          secondary: semanticDark.text.recessive,
          ternary: greyDark[500],
          disabled: greyDark[400]
        },
        // Legacy color palettes (kept for backward compatibility)
        blue,
        grey: greyDark,
        foamGreen,
        // New palette tokens from Figma
        green,
        red,
        amber,
        neutral,
        brand: semanticDark.brand,
        divider: semanticDark.border.subtle,
        background: {
          default: semanticDark.background.page,
          paper: semanticDark.background.paper,
          surface: semanticDark.background.surface,
          hover: greyDark[200],
          darkHover: greyDark[300],
          disabled: greyDark[300],
          selected: greyDark[300]
        },
        // Actionable state tokens
        actionable: semanticDark.actionable,
        // Informational state tokens
        informational: semanticDark.informational,
        // Sidenav chrome tokens
        sidenav: semanticDark.sidenav,
        // For Chip color="danger" (uses informational.danger styling)
        danger: {
          main: semanticDark.informational.danger.foreground
        }
      }
    }
  },
  colorSchemeSelector: 'data',
  typography: {
    fontFamily: interFontFamily,
    fontSize: 14,
    h1: fontStyles.displayLargeMedium,
    h2: fontStyles.displayRegularMedium,
    h3: fontStyles.displayExtraSmallMedium,
    h4: fontStyles.textLargeNormal,
    h5: fontStyles.textRegularSemibold,
    body1: fontStyles.textRegularMedium,
    body2: fontStyles.textMiniMedium,
    subtitle1: fontStyles.textMiniNormal,
    subtitle2: fontStyles.textMicroMedium,
    helper1: fontStyles.textNanoNormal,
    heading: fontStyles.heading,
    subheading: fontStyles.subheading,
    body: fontStyles.body,
    base: fontStyles.base
  },
  shadows
});

declare module '@mui/material/styles' {
  interface TypographyVariants {
    helper1: React.CSSProperties;
    heading: React.CSSProperties;
    subheading: React.CSSProperties;
    body: React.CSSProperties;
    base: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    helper1?: React.CSSProperties;
    heading?: React.CSSProperties;
    subheading?: React.CSSProperties;
    body?: React.CSSProperties;
    base?: React.CSSProperties;
  }
}
declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    elevation: true;
    outlined: true;
    info: true;
    success: true;
    warning: true;
    error: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    tertiary: true;
    danger: true;
    text: false;
    contained: false;
    outlined: false;
  }
  interface ButtonPropsSizeOverrides {
    default: true;
    small: true;
    medium: false;
    large: false;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    helper1: true;
    heading: true;
    subheading: true;
    body: true;
    base: true;
  }
}

declare module '@mui/material/styles' {
  interface CommonColors {
    natomaBlack: string;
  }

  interface BreakpointOverrides {
    '2xl': true;
    '3xl': true;
  }

  interface TypeBackground {
    /**
     * Page-level background (e.g. app chrome). From semantic background.page.
     *
     * Light: neutral[200]
     * Dark: neutral[900]
     */
    default: string;
    /**
     * Main background for components and content. From semantic background.paper.
     *
     * Light: neutral[50]
     * Dark: neutral[850]
     */
    paper: string;
    /**
     * Elevated surface (cards, modals). From semantic background.surface.
     *
     * Light: neutral.white
     * Dark: neutral[800]
     */
    surface: string;
    /**
     * Used for hover states, such as in buttons, chips, or cards
     *
     * Light Mode: #F9FAFB, grey[50]
     *
     * Dark Mode: #2A2A2A, greyDark[200]
     */
    hover: string;
    /**
     * Used for nested hover states, such as subcomponents in tables and lists.
     *
     * Light Mode: #EAECF0, grey[200]
     *
     * Dark Mode: #3A3A3A, greyDark[300]
     */
    darkHover: string;
    /**
     * Used for any selected states, similar to darkHover
     *
     * Light Mode: #EAECF0, grey[200]
     *
     * Dark Mode: #3A3A3A, greyDark[300]
     */
    selected: string;
    /**
     * Used for disabled backgrounds, such as in disabled buttons.
     *
     * Light Mode: #F2F4F7, grey[100]
     *
     * Dark Mode: #3A3A3A, greyDark[300]
     */
    disabled: string;
  }
  interface TypeText {
    /**
     * Primary text color for all typography.
     *
     * Light Mode: #101828, grey[900]
     *
     * Dark Mode: #EEEEEE, greyDark[900]
     */
    primary: string;
    /**
     * Used for secondary text, such as labels and descriptions.
     *
     * Light Mode: #475467, grey[600]
     *
     * Dark Mode: #B3B3B3, greyDark[600]
     */
    secondary: string;
    /**
     * Used for tertiary text, such as typography headers
     *
     * Light Mode: #667085, grey[500]
     *
     * Dark Mode: #9E9E9E, greyDark[500]
     */
    ternary: string;
    /**
     * Disabled text color, used for disabled text, such as placeholder text in inputs as well
     * as disabled buttons and other disabled elements.
     *
     * Light Mode: #98A2B3, grey[400]
     *
     * Dark Mode: #7B7B7B, greyDark[400]
     */
    disabled: string;
  }

  interface PaletteColor {
    25?: string;
    50?: string;
    100?: string;
    200?: string;
    300?: string;
    400?: string;
    500?: string;
    600?: string;
    700?: string;
    800?: string;
    900?: string;
    950?: string;
  }

  interface Palette {
    brand: string;
    foamGreen: typeof foamGreen;
    // New Figma palette tokens
    blue: typeof blue;
    green: typeof green;
    red: typeof red;
    amber: typeof amber;
    neutral: typeof neutral & { white: string };
    // Actionable state tokens
    actionable: {
      primary: ActionableTokens;
      secondary: ActionableTokens;
      danger: ActionableTokens;
      tertiary: ActionableTokens;
    };
    // Informational state tokens
    informational: InformationalTokens;
    // Sidenav chrome tokens
    sidenav: SidenavTokens;
    // For Chip color="danger"
    danger?: { main: string };
  }

  interface PaletteOptions {
    brand?: string;
    foamGreen: typeof foamGreen;
    // New Figma palette tokens
    blue: typeof blue;
    green?: typeof green;
    red?: typeof red;
    amber?: typeof amber;
    neutral?: typeof neutral & { white: string };
    // Actionable state tokens
    actionable?: {
      primary: ActionableTokens;
      secondary: ActionableTokens;
      danger: ActionableTokens;
      tertiary: ActionableTokens;
    };
    // Informational state tokens
    informational?: InformationalTokens;
    // Sidenav chrome tokens
    sidenav?: SidenavTokens;
    // For Chip color="danger"
    danger?: { main: string };
  }
}

export default finalTheme;
